import { assertNever, DefaultMap } from "@glideapps/ts-necessities";
import type { GroundValue, LoadingValue } from "../../common/computation-model/data";
import { asBoolean, asMaybeNumber, asString, isLoadingValue } from "../../common/computation-model/data";
import type { TableName } from "../../common/database-strings";
import type {
    ActionDescription,
    ColumnAssignment,
    ColumnType,
    ComponentDescription,
    FilterArrayTransform,
    LegacyPropertyDescription,
    MutatingScreenKind,
    PropertyDescription,
    TableColumn,
    TableGlideType,
} from "../../common/description";
import {
    ArrayTransformKind,
    getTableColumn,
    getTableName,
    isColumnWriteable,
    isPrimitiveType,
    isSingleRelationType,
    isSourceColumn,
    makeSourceColumn,
} from "../../common/description";
import { isFavoritedColumnName } from "../../common/google-sheets";
import { filterType, isArray, mapFilterUndefined, sleep } from "../../common/Support";
import type { ValueGetterOptions, WireValueGetter } from "../../wire/backends";
import type { WireAction, WireEditableValue } from "../../wire/types";
import { handlerForActionKind } from "../actions";
import { CollectingVisitor, SearchResultCollector } from "../components/find-column-uses";
import { getColumnProperty } from "../components/property-utils";
import { walkAction } from "../components/walk-app-description";
import type {
    WireActionHydrationResult,
    WireActionHydrator,
    WireActionRunner,
    WireComponentHydratorWithID,
    WireInflationBackend,
    WireRowActionHydrationValueProvider,
    WireRowComponentHydrationBackend,
    WireRowComponentInflationBackend,
    WireRowComponentValueGetter,
    WireRowHydrationValueProvider,
} from "../components/wire";
import { getColumnsUsedInColumn } from "../computed-columns";
import { getSingleRelationOrThis } from "../description-utils";
import { handlerForComponentKind } from "../handlers";

export function isNotEmptyDisplayValue(v: GroundValue | null): boolean {
    return v !== undefined && v !== null && v !== "";
}

type ActionHydrationResult = [runner: WireActionRunner, url: string | undefined] | LoadingValue | undefined;

export function hydrateAction(
    hydrator: WireActionHydrator | undefined,
    vp: WireRowActionHydrationValueProvider,
    treatLoadingAsUndefined: boolean
): ActionHydrationResult {
    if (hydrator === undefined) return undefined;

    const maybeRunner = hydrator(vp, treatLoadingAsUndefined);
    if (maybeRunner === undefined || isLoadingValue(maybeRunner)) return maybeRunner;

    if (isArray(maybeRunner)) {
        return maybeRunner;
    } else {
        return [maybeRunner, undefined];
    }
}

export function hydrateActionWithRunnerAfter(
    hydrator: WireActionHydrator | undefined,
    vp: WireRowActionHydrationValueProvider,
    treatLoadingAsUndefined: boolean,
    runnerAfter: WireActionRunner,
    urlAfter: string | undefined
): ActionHydrationResult {
    const hydrationResult = hydrateAction(hydrator, vp, treatLoadingAsUndefined);
    if (isLoadingValue(hydrationResult)) return hydrationResult;
    const [actionRunner, actionURL] = hydrationResult ?? [];

    return [
        async (ab, handled) => {
            // If `handled` is set, then it can apply to either the
            // action, or to the link we have to open, depending on
            // whether the action has a URL in the first place.
            //
            // If we have an `actionURL`, then `handled` refers to
            // that URL because we do that action first, otherwise it
            // refers to the `urlAfter`
            if (actionURL !== undefined && actionRunner !== undefined && !handled) {
                const result = await actionRunner(ab, false);
                if (result === false) return false;
            }
            return await runnerAfter(ab, actionURL === undefined && handled);
        },
        actionURL ?? urlAfter,
    ];
}

async function hydrateActionWithTimeout(
    hydrator: WireActionHydrator | undefined,
    vp: WireRowActionHydrationValueProvider,
    timesOutAt: Date
): Promise<[runner: WireActionRunner, url: string | undefined] | undefined> {
    // TODO: This is a somewhat shitty way to do this.  The proper way is to
    // subscribe to all the necessary paths and try hydrating the action on
    // every update, which will produce a new set of paths to subscribe to.
    while (new Date() < timesOutAt) {
        const result = hydrateAction(hydrator, vp, false);
        if (result === undefined) return undefined;
        if (isLoadingValue(result)) {
            await sleep(10);
            continue;
        }
        return result;
    }
    const finalResult = hydrateAction(hydrator, vp, true);
    if (isLoadingValue(finalResult)) return undefined;
    return finalResult;
}

export function makeActionRunner(
    hydrators: readonly (WireActionHydrator | undefined)[],
    useURL: boolean,
    vp: WireRowActionHydrationValueProvider,
    noLoading: boolean
): WireActionHydrationResult {
    if (hydrators.length === 0) return undefined;

    const [firstHydrator, ...otherHydrators] = hydrators;

    const maybeFirstRunner = hydrateAction(firstHydrator, vp, noLoading);
    if (maybeFirstRunner === undefined) return undefined;
    if (isLoadingValue(maybeFirstRunner)) {
        return noLoading ? undefined : maybeFirstRunner;
    }
    const [firstRunner, maybeURL] = maybeFirstRunner;
    const url = useURL ? maybeURL : undefined;

    return [
        async (ab, handled) => {
            if (!(await firstRunner(ab, handled && url !== undefined))) return false;

            for (const hydrator of otherHydrators) {
                const hb = ab.makeBackendForSubAction();
                const maybeRunner = await hydrateActionWithTimeout(hydrator, hb, ab.timesOutAt);
                if (maybeRunner === undefined) return false;
                const [runner] = maybeRunner;

                if (!(await runner(ab, false))) return false;
            }

            return true;
        },
        url,
    ];
}

export function hydrateOpenURL(url: string): [WireActionRunner, string] {
    return [
        async (ab, handled) => {
            if (!handled) {
                ab.actionCallbacks.openLink(url);
            }
            return true;
        },
        url,
    ];
}

function getAllTablesUsedInActions(
    ib: WireInflationBackend,
    actions: readonly ActionDescription[],
    mutatingScreenKind: MutatingScreenKind | undefined,
    actionIsIndirect: boolean
): ReadonlySet<TableGlideType> {
    const toVisit: [TableGlideType, string][] = [];

    class Visitor extends CollectingVisitor {
        protected shouldAddColumnUsed(tn: TableName, cn: string, isWrite: boolean): boolean {
            if (isWrite) return false;

            const table = ib.adc.findTable(tn);
            if (table === undefined) return false;

            toVisit.push([table, cn]);

            return true;
        }
    }

    const collector = new SearchResultCollector();
    const visitor = new Visitor(ib.adc, collector);

    for (const action of actions) {
        walkAction(ib.adc, visitor, action, ib.tables, mutatingScreenKind, actionIsIndirect, true);
    }

    const tablesAndColumns = new DefaultMap<TableGlideType, Set<TableColumn>>(() => new Set());

    for (;;) {
        const tableAndColumn = toVisit.pop();
        if (tableAndColumn === undefined) break;

        const [table, columnName] = tableAndColumn;
        const column = getTableColumn(table, columnName);
        if (column === undefined) continue;

        if (tablesAndColumns.get(table).has(column)) continue;

        tablesAndColumns.get(table).add(column);

        for (const [t, cns] of getColumnsUsedInColumn(ib.adc, getTableName(table), column)[0]) {
            for (const cn of cns) {
                toVisit.push([t, cn]);
            }
        }
    }

    return new Set(tablesAndColumns.keys());
}

export function inflateActions(
    ib: WireInflationBackend,
    actions: readonly ActionDescription[]
): WireActionHydrator | undefined {
    const hydrators = mapFilterUndefined(actions, a => {
        const handler = handlerForActionKind(a.kind);
        return handler?.inflate?.(ib, a);
    });
    if (hydrators.length === 0) return undefined;

    // I don't think it's a problem that we're not passing in the mutating
    // screen kind or the indirect flag here.
    const tables = getAllTablesUsedInActions(ib, actions, undefined, false);

    return (vp, noLoading) => {
        for (const table of tables) {
            vp.fetchTable(getTableName(table));
        }
        return makeActionRunner(hydrators, true, vp, noLoading);
    };
}

export type OutputValueGetters = readonly [string, WireRowComponentValueGetter][];

export function inflateColumnAssignments(
    ib: WireInflationBackend,
    outputTableName: TableName,
    columnAssignments: readonly ColumnAssignment[],
    allowUserSpecificColumns: boolean
): OutputValueGetters | undefined {
    const outputTable = ib.adc.findTable(outputTableName);
    if (outputTable === undefined) return undefined;

    const targetColumnNames = new Set(
        outputTable.columns
            .filter(c => isColumnWriteable(c, outputTable, allowUserSpecificColumns, true))
            .map(c => c.name)
    );

    const outputValueGetters: [string, WireRowComponentValueGetter][] = [];
    for (const a of columnAssignments) {
        if (!targetColumnNames.has(a.destColumn)) continue;

        const [getter, type] = ib.getValueGetterForProperty(a.value, false);
        if (!isPrimitiveType(type)) continue;
        outputValueGetters.push([a.destColumn, getter]);
    }

    return outputValueGetters;
}

export function inflateComponent(
    ib: WireRowComponentInflationBackend,
    desc: ComponentDescription,
    visibilityFromOutputRow: boolean
): WireComponentHydratorWithID | undefined {
    const handler = handlerForComponentKind(desc.kind);
    const hydrator = handler?.inflate?.(ib, desc);
    if (hydrator === undefined) return undefined;

    const { componentID } = desc;

    const filters = filterType(
        desc.visibilityFilters ?? [],
        (t): t is FilterArrayTransform => t.kind === ArrayTransformKind.Filter
    );
    if (filters.length === 0) {
        return [hydrator, componentID];
    }

    const visibilityPredicate = ib.inflateFilters(filters, visibilityFromOutputRow, undefined);
    return [
        hb => {
            const isVisible = visibilityPredicate(hb);
            if (isLoadingValue(isVisible) || !isVisible) return undefined;
            return hydrator(hb);
        },
        componentID,
    ];
}

export function registerActionRunner(
    hb: WireRowComponentHydrationBackend,
    // This must be unique for this action in this component.
    key: string,
    makeRunner: () => WireActionHydrationResult
): WireAction | undefined {
    const isBusy = hb.getState(`busy-${key}`, (v): v is boolean => typeof v === "boolean", false, false);
    if (isBusy.value) {
        return { token: null, url: undefined };
    }

    const maybeRunner = makeRunner();
    if (isLoadingValue(maybeRunner) || maybeRunner === undefined) return undefined;

    const [runner, url] = isArray(maybeRunner) ? maybeRunner : [maybeRunner];
    const token = hb.registerAction(async (ab, handled) => {
        ab.valueChanged(isBusy.onChangeToken, true);
        try {
            return await runner(ab, handled);
        } finally {
            ab.valueChanged(isBusy.onChangeToken, false);
        }
    });
    return { token, url };
}

export function spreadComponentID(componentID: string | undefined, forBuilder: boolean): { readonly id?: string } {
    if (!forBuilder || componentID === undefined) {
        return {};
    } else {
        return { id: componentID };
    }
}

// `null` means unbound
export type WireStringGetter = (hb: WireRowHydrationValueProvider) => string | null;
// `undefined` means could not be converted to number
export type WireNumberGetter = (hb: WireRowHydrationValueProvider) => number | undefined | null;

export function inflateStringProperty(
    ib: WireInflationBackend,
    propertyDesc: unknown,
    withFormat: boolean,
    opts?: ValueGetterOptions
): [WireStringGetter, ColumnType | undefined] {
    const [getter, type] = ib.getValueGetterForProperty(propertyDesc, withFormat, opts);
    return [
        hb => {
            const v = getter(hb);
            if (v === null) return null;
            if (isLoadingValue(v)) return "";
            return asString(v);
        },
        type,
    ];
}

export function inflateNumberProperty(
    ib: WireInflationBackend,
    propertyDesc: unknown,
    opts?: ValueGetterOptions
): [WireNumberGetter, ColumnType | undefined] {
    const [getter, type] = ib.getValueGetterForProperty(propertyDesc, false, opts);
    return [
        hb => {
            const v = getter(hb);
            if (v === null) return null;
            if (isLoadingValue(v)) return undefined;
            return asMaybeNumber(v);
        },
        type,
    ];
}

export function inflateFavorite(
    ib: WireInflationBackend,
    primaryKeyDesc: PropertyDescription | undefined
): ((hb: WireRowComponentHydrationBackend) => WireEditableValue<boolean>) | undefined {
    const [getter, type] = ib.getValueGetterForColumn(makeSourceColumn(isFavoritedColumnName), true, false);
    if (type === undefined) return undefined;

    const primaryKeyColumnName = getColumnProperty(primaryKeyDesc);

    return hb => {
        const isFavorited = getter(hb);
        if (isFavorited === null || isLoadingValue(isFavorited)) {
            return {
                value: false,
                onChangeToken: undefined,
            };
        }
        const onChangeToken = hb.registerOnValueChange(isFavoritedColumnName, primaryKeyColumnName);
        return {
            value: asBoolean(isFavorited),
            onChangeToken,
        };
    };
}

// `false` means there was an error
export function makeGetterForSourceRelationOrThis(
    ib: WireInflationBackend,
    desc: LegacyPropertyDescription | undefined,
    defaultToThisRow: boolean
): [WireValueGetter, TableGlideType] | undefined | false {
    const singleRelationOrThis = getSingleRelationOrThis(desc);

    if (isSourceColumn(singleRelationOrThis)) {
        const [getter, type] = ib.getValueGetterForProperty(desc, false);
        if (type === undefined || !isSingleRelationType(type)) return false;
        const table = ib.adc.findTable(type);
        if (table === undefined) return false;

        return [getter, table];
    } else if (singleRelationOrThis === true || (defaultToThisRow && singleRelationOrThis === undefined)) {
        return [vp => vp.rowContext?.inputRow, ib.tables.input];
    } else if (singleRelationOrThis === undefined) {
        return undefined;
    } else {
        return assertNever(singleRelationOrThis);
    }
}
